import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Button, DatePicker } from '@/components';
import { SearchableDropdown } from '@/components/SearchableDropdown';
import { SideBarModal } from '@/components/Modal';
import { getListOem } from '@/services/api/oems';
import { getListModel } from '@/services/api/models';
import {
  extractApiErrorMessage,
  fileToBase64,
  getBucketUrlImageV2,
  openImageFileDialog,
  validateImageFile,
  uploadFileImage,
} from '@/utils';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { Model, Oem } from '@/types/common';

interface TempSwapPayload {
  tempPlateNumber: string;
  tempModelName: string;
  tempOemName: string;
  tempSwapGivenAt: string;
  tempSwapPhotos: string[];
  tempSwapNotes: string;
}

interface TempFileData {
  name: string;
  size: number;
  base64?: string;
}

interface RequestTempSwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: TempSwapPayload) => void;
  isSubmitting?: boolean;
}

const CLOUD_ICON_PATH =
  'M3 15a4 4 0 004 4h10a4 4 0 000-8 5 5 0 00-9.7-1.7A4.5 4.5 0 003 15z';

const inputClassName = (hasError: boolean) =>
  `w-full rounded-md border px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 ${
    hasError
      ? 'border-red-500 focus:ring-red-200'
      : 'border-gray-200 focus:ring-purple-200'
  }`;

const hintClassName = (hasError: boolean) =>
  `mt-1 text-[11px] ${hasError ? 'text-red-500' : 'text-gray-400'}`;

const formatFileSize = (size?: number) => {
  if (!size && size !== 0) return '';
  if (size < 1024) return `${size} B`;
  const kb = size / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const CloudIcon: React.FC<{ className?: string }> = ({
  className = 'w-12 h-12',
}) => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d={CLOUD_ICON_PATH}
    />
  </svg>
);

const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
  className?: string;
}> = ({ children, required, className = 'mb-1' }) => (
  <label className={`block text-xs font-medium text-gray-700 ${className}`}>
    {children}
    {required && <span className="text-red-500"> *</span>}
  </label>
);

const ErrorHint: React.FC<{ show: boolean; message?: string }> = ({
  show,
  message = 'Required',
}) =>
  show ? <div className="mt-1 text-[11px] text-red-500">{message}</div> : null;

interface ImageUploadZoneProps {
  isUploading: boolean;
  tempFile: TempFileData | null;
  hasError: boolean;
  isDragActive: boolean;
  onBrowse: () => void;
  onRemove: () => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
}

const ImageUploadZone: React.FC<ImageUploadZoneProps> = ({
  isUploading,
  tempFile,
  hasError,
  isDragActive,
  onBrowse,
  onRemove,
  onDragOver,
  onDragLeave,
  onDrop,
}) => (
  <>
    <div
      className={`border-2 border-dashed rounded-lg w-full ${
        hasError ? 'border-red-500' : 'border-gray-300'
      }`}
    >
      {isUploading ? (
        <div className="p-8 flex flex-col justify-center items-center text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          <p className="mt-2 text-sm text-gray-600">Uploading photo...</p>
        </div>
      ) : !tempFile ? (
        <div
          onClick={onBrowse}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`p-8 flex flex-col justify-center items-center text-center cursor-pointer hover:bg-blue-50 transition-all ${
            isDragActive ? 'bg-blue-50' : ''
          }`}
        >
          <div className="text-gray-400 mb-4">
            <CloudIcon />
          </div>
          <p className="font-semibold text-gray-700">
            Choose a file or drag & drop it here.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Image format, up to 10 MB.
          </p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onBrowse();
            }}
            className="mt-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Browse File
          </button>
        </div>
      ) : (
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-gray-600">
              <CloudIcon className="w-8 h-8" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {tempFile.name || 'Temporary Swap Photo'}
              </p>
              <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                <span>{formatFileSize(tempFile.size)}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-green-500"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span className="text-green-600 font-medium">Completed</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="text-gray-500 hover:text-red-500 transition-colors"
          >
            <svg
              className="w-5 h-5 text-red-500 hover:text-red-700 cursor-pointer"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
    <ErrorHint show={hasError} />
  </>
);

const RequestTempSwapModal: React.FC<RequestTempSwapModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [tempPlateNumber, setTempPlateNumber] = useState('');
  const [tempOemName, setTempOemName] = useState('');
  const [tempModelName, setTempModelName] = useState('');
  const [tempOemId, setTempOemId] = useState('');
  const [tempModelId, setTempModelId] = useState('');
  const [tempSwapGivenAt, setTempSwapGivenAt] = useState('');
  const [tempSwapNotes, setTempSwapNotes] = useState('');
  const [tempSwapPhotos, setTempSwapPhotos] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [tempFile, setTempFile] = useState<TempFileData | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [oems, setOems] = useState<Oem[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [loadingOems, setLoadingOems] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setTempPlateNumber('');
      setTempOemName('');
      setTempModelName('');
      setTempOemId('');
      setTempModelId('');
      setTempSwapGivenAt('');
      setTempSwapNotes('');
      setTempSwapPhotos([]);
      setIsUploading(false);
      setIsDragActive(false);
      setTempFile(null);
      setShowErrors(false);
      setOems([]);
      setModels([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const loadOems = async () => {
      setLoadingOems(true);
      try {
        const response = await getListOem({
          page: '1',
          size: '1000',
          isActive: 'true',
        });
        setOems(response.data || []);
      } catch (error) {
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load OEMs')
        );
        setOems([]);
      } finally {
        setLoadingOems(false);
      }
    };

    if (isOpen) {
      loadOems();
    }
  }, [isOpen]);

  const loadModelsForOem = async (oemId: string) => {
    if (!oemId) {
      setModels([]);
      return;
    }
    setLoadingModels(true);
    try {
      const response = await getListModel({
        page: '1',
        size: '1000',
        oemIDs: oemId,
      });
      setModels(response.data || []);
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to load models')
      );
      setModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleOemChange = async (oemId: string) => {
    const selected = oems.find((item) => item.ID === oemId);
    setTempOemId(oemId);
    setTempOemName(selected?.name || '');
    setTempModelId('');
    setTempModelName('');
    await loadModelsForOem(oemId);
  };

  const handleModelChange = (modelId: string) => {
    const selected = models.find((item) => item.ID === modelId);
    setTempModelId(modelId);
    setTempModelName(selected?.name || '');
  };

  const oemOptions = useMemo(
    () =>
      oems.map((oem) => ({
        value: oem.ID,
        label: oem.name,
      })),
    [oems]
  );

  const modelOptions = useMemo(
    () =>
      models.map((model) => ({
        value: model.ID,
        label: model.name,
      })),
    [models]
  );

  const uploadImage = async (fileData: TempFileData, base64Data?: string) => {
    const uuid = uuidv4();
    const response = await getBucketUrlImageV2({ type: 'maintenance', uuid });

    if (!response?.data) throw new Error('Failed to get upload URL');

    const payload = base64Data || fileData.base64?.split(',')[1];
    if (!payload) throw new Error('Invalid file data');

    const upload = await uploadFileImage(response.data, payload, fileData.name);
    if (upload.status !== 200) throw new Error('Upload failed');

    setTempSwapPhotos([response.data]);
    setTempFile(fileData);
    showSuccessToast('Success', 'Upload image success');
  };

  const handleUploadError = (error: unknown) => {
    showErrorToast(
      'Error',
      extractApiErrorMessage(error, 'Failed to upload image')
    );
    setTempSwapPhotos([]);
    setTempFile(null);
  };

  const handleBrowse = () => {
    openImageFileDialog(async (file) => {
      if (!file) return;
      setIsUploading(true);
      try {
        await uploadImage(file);
      } catch (error) {
        handleUploadError(error);
      } finally {
        setIsUploading(false);
      }
    });
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (!validateImageFile(file)) {
      showErrorToast('Error', 'Invalid image file');
      return;
    }

    setIsUploading(true);
    try {
      const base64 = await fileToBase64(file);
      await uploadImage({ name: file.name, size: file.size }, base64);
    } catch (error) {
      handleUploadError(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setTempSwapPhotos([]);
    setTempFile(null);
  };

  const isFormValid =
    tempPlateNumber.trim() &&
    tempOemName.trim() &&
    tempModelName.trim() &&
    tempSwapGivenAt &&
    tempSwapPhotos.length > 0;

  const errors = {
    tempPlateNumber: showErrors && !tempPlateNumber.trim(),
    tempOemName: showErrors && !tempOemName.trim(),
    tempModelName: showErrors && !tempModelName.trim(),
    tempSwapGivenAt: showErrors && !tempSwapGivenAt,
    tempSwapPhotos: showErrors && tempSwapPhotos.length === 0,
  };

  const handleSubmit = () => {
    if (!isFormValid) {
      setShowErrors(true);
      return;
    }

    onSubmit({
      tempPlateNumber: tempPlateNumber.trim(),
      tempOemName: tempOemName.trim(),
      tempModelName: tempModelName.trim(),
      tempSwapGivenAt,
      tempSwapPhotos,
      tempSwapNotes: tempSwapNotes.trim(),
    });
  };

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      width="sm"
      maxHeight="screen"
      showCloseButton
      className="max-w-[420px]"
      header={
        <div>
          <div className="text-base font-semibold text-gray-900">
            Request Temporary Swap
          </div>
          <div className="text-xs text-gray-500">
            Assign a temporary replacement unit to the driver to ensure
            operational continuity while the primary vehicle is under repair.
          </div>
        </div>
      }
      footer={
        <div className="flex w-full justify-end">
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting || isUploading}
            className="w-full"
            variant="primary"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="bg-gray-50 px-4 py-2 text-[12px] font-medium text-gray-500 rounded-md">
        Detail
      </div>

      <div className="px-6 py-4 space-y-4">
        <div>
          <FieldLabel required>Temporary Plate Number</FieldLabel>
          <input
            value={tempPlateNumber}
            onChange={(e) => setTempPlateNumber(e.target.value)}
            placeholder="e.g. B 1234 ABC"
            className={inputClassName(errors.tempPlateNumber)}
          />
          <div className={hintClassName(errors.tempPlateNumber)}>Required</div>
        </div>

        <div>
          <FieldLabel required>OEM Name</FieldLabel>
          <SearchableDropdown
            placeholder={loadingOems ? 'Loading OEMs...' : 'e.g. Electrum'}
            options={oemOptions}
            value={tempOemId}
            onChange={(value) => handleOemChange(value as string)}
            disabled={loadingOems}
            error={errors.tempOemName}
          />
        </div>

        <div>
          <FieldLabel required>Model Name</FieldLabel>
          <SearchableDropdown
            placeholder={
              loadingModels
                ? 'Loading models...'
                : tempOemId
                  ? 'e.g. H3'
                  : 'Select OEM first'
            }
            options={modelOptions}
            value={tempModelId}
            onChange={(value) => handleModelChange(value as string)}
            disabled={!tempOemId || loadingModels}
            error={errors.tempModelName}
          />
        </div>

        <div>
          <FieldLabel required>Temporary Swap Date</FieldLabel>
          <DatePicker
            showTimeSelect
            value={tempSwapGivenAt}
            onChange={setTempSwapGivenAt}
            className="w-full"
            error={errors.tempSwapGivenAt}
            helperText={errors.tempSwapGivenAt ? 'Required' : undefined}
          />
        </div>

        <div>
          <FieldLabel required>Temporary Swap Vehicle Photos</FieldLabel>
          <ImageUploadZone
            isUploading={isUploading}
            tempFile={tempFile}
            hasError={errors.tempSwapPhotos}
            isDragActive={isDragActive}
            onBrowse={handleBrowse}
            onRemove={handleRemoveImage}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragActive(true);
            }}
            onDragLeave={() => setIsDragActive(false)}
            onDrop={handleDrop}
          />
        </div>

        <div>
          <FieldLabel>Additional Notes</FieldLabel>
          <textarea
            value={tempSwapNotes}
            onChange={(e) => setTempSwapNotes(e.target.value)}
            placeholder="Describe the temporary unit's current condition or any specific instructions given to the driver."
            rows={3}
            className={inputClassName(false)}
          />
          <div className="mt-1 text-[11px] text-gray-400">
            {Math.min(tempSwapNotes.length, 200)}/200
          </div>
        </div>
      </div>
    </SideBarModal>
  );
};

export default RequestTempSwapModal;
